/**
 * Single source of truth for theme utilities.
 * Consumed by main.tsx (bootstrap) and AppRuntime.tsx (runtime toggling).
 */

export const THEME_STORAGE_KEY = "dc_theme_preference";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export function isThemePreference(value: string | null | undefined): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

export function resolveSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "light";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === "system" ? resolveSystemTheme() : preference;
}

export function readThemePreference(): ThemePreference {
  if (typeof window === "undefined") {
    return "system";
  }
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function hasStoredThemePreference(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return isThemePreference(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return false;
  }
}

/**
 * Resolve preference to a concrete theme, then set data-theme, data-theme-preference,
 * and color-scheme on document.documentElement.
 */
export function applyThemePreference(preference: ThemePreference): ResolvedTheme {
  const theme = resolveTheme(preference);
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.dataset.themePreference = preference;
  root.style.colorScheme = theme;
  return theme;
}
